import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { flexRender, type ColumnFiltersState } from '@tanstack/react-table'

import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react'
import { cn } from '~/shadcn/lib/utils'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/shadcn/ui/table'
import { FiltersContainer } from '~/@mid_ui/table/FiltersContainer'
import { FilterToggleButton } from '~/@mid_ui/table/FiltersToggle'
import { getColumnFilterLength } from '~/@mid_ui/table/getColumnFilterLength'
import { useIsDesktop } from '~/@mid_ui/layout/useIsDesktop'
import { PageContainer } from '~/@mid_ui/page-container/PageContainer'
import { NoInvestments } from '../investments_ui/no-investments/NoInvestments'
import { TabTitleWrapper } from '../../financial-overview/TabTitleWrapper'
import { InvestmentsFilters } from '../_investments_filtering/InvestmentsFilters'
import { useFilters } from '../_investments_filtering/useFilters'
import { mapTableFiltersToRemoteFilter } from './mapTableFiltersToRemoteFilter'
import { useInvestmentsTable } from './useInvestmentsTable'
import { useInvestmentsQuery } from './useInvestmentsQuery'
import { Spinner } from '~/shadcn/ui/spinner'
import { HeadingTwo } from '~/@mid_ui/typography/HeadingTwo'
import { Button } from '~/shadcn/ui/button/button'

type Props = {
  investmentId: string | null
}

function InvestmentsTable({ investmentId }: Props) {
  const isDesktop = useIsDesktop()
  const navigate = useNavigate()

  const {
    isFilterOpen,
    toggleFilterOpen,

    sorting,
    handleSortingChange,
  } = useFilters()

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [initialized, setInitialized] = useState<boolean>(false)

  const remoteFilters = (() => {
    const filters = mapTableFiltersToRemoteFilter(columnFilters)

    return investmentId ? { ...filters, _id: investmentId } : filters
  })()

  const {
    query: { data, isFetching: isLoading, fetchNextPage, hasNextPage },
  } = useInvestmentsQuery({
    filter: remoteFilters,
    sort: sorting[0] as never,
  })

  const investments = useMemo(
    () => data?.pages.flatMap(({ items }) => items) ?? [],
    [data?.pages]
  )

  const table = useInvestmentsTable(investments, {
    columnFilters,
    onColumnFiltersChange: setColumnFilters,
    sorting,
    onSortingChange: handleSortingChange,
  })

  const columnFiltersLength = getColumnFilterLength(table)
  const activeFiltersCount = (() =>
    columnFiltersLength ? ` (${columnFiltersLength})` : '')()
  const showNoInvestments =
    investments.length === 0 && !activeFiltersCount && !isLoading

  useEffect(() => {
    if (initialized || isLoading) return

    setInitialized(true)
  }, [initialized, data, isLoading])

  if (!initialized) {
    return (
      <div className="flex justify-center items-center mt-7 md:mt-10">
        <Spinner className="size-6" />
      </div>
    )
  }

  return (
    <>
      <div className="md:border-b border-border">
        {isDesktop ? (
          <TabTitleWrapper>
            <HeadingTwo>Moje investície</HeadingTwo>
            {investments.length > 0 && (
              <FilterToggleButton
                toggleFilterOpen={toggleFilterOpen}
                isFilterOpen={isFilterOpen}
                activeFiltersCount={activeFiltersCount}
              />
            )}
          </TabTitleWrapper>
        ) : (
          investments.length > 0 && (
            <div className="border-b border-border">
              <div className="px-5 py-7">
                <FilterToggleButton
                  toggleFilterOpen={toggleFilterOpen}
                  isFilterOpen={isFilterOpen}
                  activeFiltersCount={activeFiltersCount}
                />
              </div>
            </div>
          )
        )}
      </div>

      {isFilterOpen && (
        <FiltersContainer colorScheme="light">
          <PageContainer>
            <InvestmentsFilters table={table} />
          </PageContainer>
        </FiltersContainer>
      )}

      <PageContainer className="pb-15 md:pb-30">
        {showNoInvestments && <NoInvestments />}
        {!showNoInvestments && (
          <>
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={`TableRow-${headerGroup.id}`}
                    className="border-b border-border">
                    {headerGroup.headers.map((header, index) => {
                      if (header.isPlaceholder) {
                        return <TableHead key={`TableHead-${header.id}`} />
                      }

                      const canSort = header.column.getCanSort()
                      const sortingOrder = header.column.getNextSortingOrder()
                      const isSorted = header.column.getIsSorted()

                      const orderByText = {
                        asc: 'Zoradiť vzostupne',
                        desc: 'Zoradiť zostupne',
                      }

                      const title = canSort
                        ? sortingOrder
                          ? orderByText[sortingOrder]
                          : 'Zrušiť triedenie'
                        : undefined

                      const sortingIcon = {
                        asc: <ArrowUpIcon className="h-4 w-4" />,
                        desc: <ArrowDownIcon className="h-4 w-4" />,
                      }[isSorted || 'desc']

                      return (
                        <TableHead
                          key={`TableHead-${header.id}`}
                          className={cn(
                            index === 0 &&
                              'border-r flex justify-self-end pl-1 md:pr-3 border-b-0',
                            header.id === 'value' ? 'bg-[#F1FFEB]' : '',
                            'border-border'
                          )}>
                          <div
                            className={`flex items-center ${
                              canSort ? 'cursor-pointer' : ''
                            }`}
                            title={title}
                            onClick={header.column.getToggleSortingHandler()}>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {canSort && (
                              <span
                                className={`ml-2 ${
                                  isSorted
                                    ? 'text-[#121212]'
                                    : 'text-muted-foreground'
                                }`}>
                                {sortingIcon}
                              </span>
                            )}
                          </div>
                        </TableHead>
                      )
                    })}
                  </TableRow>
                ))}
              </TableHeader>

              <TableBody className="border-b border-border">
                {table.getRowModel().rows.map((row) => {
                  return (
                    <TableRow
                      key={`TableRow-${row.id}`}
                      onClick={(
                        event: React.MouseEvent<HTMLTableRowElement>
                      ) => {
                        event.preventDefault()

                        navigate(
                          `/projects/${row.original.project.businessId}/${row.original.project.stageNumber}`
                        )
                      }}
                      className={`h-20 cursor-pointer 
                        odd:bg-foreground/8 even:bg-transparent hover:bg-accent/25
                        even:hover:bg-[rgba(0,110,240,0.2)]`}>
                      {row.getVisibleCells().map((cell, index) => (
                        <TableCell
                          key={`TableCell-${cell.id}`}
                          className={cn(
                            index === 0 && 'border-r border-border pl-1',
                            cell.column.id === 'value' ? 'bg-[#F1FFEB]' : ''
                          )}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>

            <div className="flex justify-center items-center pt-7 md:pt-10">
              {hasNextPage && (
                <Button
                  variant="outline"
                  disabled={isLoading}
                  onClick={() => fetchNextPage()}>
                  Zobraziť ďalšie investície
                </Button>
              )}
            </div>
          </>
        )}
      </PageContainer>
    </>
  )
}

export { InvestmentsTable }
